import { Fragment, useEffect, useRef, useState } from 'react'
import type { CSSProperties, FC, ImgHTMLAttributes, ReactNode } from 'react'
import { useDebounce } from '../hooks'

export interface IImageWithFallbackPropsOwner {
  /** The source URL of the image */
  src?: string
  /** The alt text for the image */
  alt?: string
  /** The fallback source(s) to use if the main image fails to load */
  fallbackSrc?: string | string[]
  /** The loading indicator to display while the image is loading */
  loading?: ReactNode | ((isLoading: boolean) => ReactNode)
  /** The debounce delay in milliseconds */
  debounceDelay?: number
}

export type IImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, keyof IImageWithFallbackPropsOwner>

export interface IImageWithFallbackProps extends IImageProps, IImageWithFallbackPropsOwner {}

export const ImageWithFallback: FC<IImageWithFallbackProps> = ({ src, fallbackSrc, alt, loading, debounceDelay = 0, ...props }) => {
  const debouncedSrc = useDebounce(src, debounceDelay)
  const debouncedFallbackSrc = useDebounce(fallbackSrc, debounceDelay)

  const effectiveSrc = debounceDelay > 0 ? debouncedSrc : src
  const effectiveFallbackSrc = debounceDelay > 0 ? debouncedFallbackSrc : fallbackSrc

  // Normalize fallbackSrc to array for uniform iteration
  const fallbackList = effectiveFallbackSrc ? (Array.isArray(effectiveFallbackSrc) ? effectiveFallbackSrc : [effectiveFallbackSrc]) : []

  // -1 when src is provided (no fallback tried yet); 0 when starting directly from fallbackList[0]
  const fallbackIndexRef = useRef(effectiveSrc ? -1 : 0)

  const [isLoading, setLoading] = useState(true)
  const [imgSrc, setImgSrc] = useState(effectiveSrc ?? fallbackList[0])

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Safety net: force-stop loading after 2s in case onLoad/onError never fires (e.g. broken network)
  const forceStopLoading = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    timeoutRef.current = setTimeout(() => {
      setLoading(false)
    }, 2000)
  }

  // Cleanup pending timeout to avoid setState on unmounted component
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  // Reset state whenever src or fallbackSrc changes
  useEffect(() => {
    fallbackIndexRef.current = effectiveSrc ? -1 : 0
    setImgSrc(effectiveSrc ?? fallbackList[0])
    setLoading(true)
  }, [effectiveSrc, effectiveFallbackSrc])

  const loadingElement = loading ? (typeof loading === 'function' ? loading(isLoading) : isLoading ? loading : null) : null

  const mapStyle = () => {
    const obj: { style?: CSSProperties } = {}
    if (props.style) obj.style = props.style
    if (isLoading) obj.style = { ...obj.style, height: '0!important', transition: 'height 0.2s ease-in-out' }
    return obj
  }

  // Try next fallback in sequence; stop loading when the list is exhausted
  const handleError = () => {
    const nextIndex = fallbackIndexRef.current + 1
    if (nextIndex < fallbackList.length) {
      fallbackIndexRef.current = nextIndex
      setImgSrc(fallbackList[nextIndex])
    } else {
      setLoading(false)
      forceStopLoading()
    }
  }

  return (
    <Fragment>
      {loadingElement}
      <img
        {...props}
        {...mapStyle()}
        src={imgSrc}
        alt={alt}
        onLoadStart={() => setLoading(true)}
        onLoad={() => {
          setLoading(false)
          forceStopLoading()
        }}
        onError={handleError}
      />
    </Fragment>
  )
}
export default ImageWithFallback
